import PortalIdParser from 'PortalIdParser';
import { ComposableCardResponseSectionTypename, SourcesListResponseSectionTypename, TextFieldBlockTypename, TimelineBlockTypename } from '../../../__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
import { reportError } from '../reportError';
import { convertDisplayOnlyEntryToCitation, convertToSearchResultMarkers, createCiteMarkerRegex, extractCiteId, isCitationCompanionEntry } from './citationUtils';
function asArray(value) {
  return Array.isArray(value) ? value : [];
}
function isRecord(value) {
  return typeof value === 'object' && value !== null;
}
const reportedMarkers = new Set();
export function _resetReportedMarkers() {
  reportedMarkers.clear();
}
function buildCitationIdToEntryMap(messages) {
  return new Map(messages.flatMap(msg => msg.blocks).filter(block => block.type === 'DISPLAY_ONLY').flatMap(block => {
    var _block$entries;
    return (_block$entries = block.entries) !== null && _block$entries !== void 0 ? _block$entries : [];
  }).filter(entry => typeof entry.citationId === 'string').map(entry => [entry.citationId, entry]));
}

// Two delivery paths set __typename differently:
// - BZE citationId path: toDisplayOnlyEntry maps the `type` field value 'SOURCES_LIST' to
//   the FQ typename via BZE_SHORT_TYPE_TO_TYPENAME, so the first check handles it.
// - Ably list path: the plain-JSON __typename is 'SourcesListResponseSection' (a partial
//   class name). toDisplayOnlyEntry picks it up directly from getString(value, '__typename')
//   before reaching the `type` fallback, so BZE_SHORT_TYPE_TO_TYPENAME never fires and the
//   entry keeps the short name. value.type === 'SOURCES_LIST' is the fallback for this path.
function isSourcesListEntry(entry) {
  if (entry.__typename === SourcesListResponseSectionTypename) return true;
  const value = entry.value;
  return (value === null || value === void 0 ? void 0 : value.type) === 'SOURCES_LIST';
}

// Returns the sourcesSections arrays from a DISPLAY_ONLY block, handling two delivery paths:
//   1. entries path: block has decoded entries (CHIRP history and Ably streaming both land
//      here) — match by SOURCES_LIST shape so only sources-list entries contribute
//   2. text path: parseDisplayOnlyBlock could not type the value and stored it as block.text
function getSourcesSectionsFromBlock(block) {
  var _block$entries2;
  const fromEntries = ((_block$entries2 = block.entries) !== null && _block$entries2 !== void 0 ? _block$entries2 : []).filter(isSourcesListEntry).flatMap(entry => {
    var _entry$value;
    return asArray((_entry$value = entry.value) === null || _entry$value === void 0 ? void 0 : _entry$value.sourcesSections);
  });
  if (fromEntries.length > 0) return fromEntries;
  if (!block.text) return [];
  try {
    const parsed = JSON.parse(block.text);
    return asArray(parsed === null || parsed === void 0 ? void 0 : parsed.sourcesSections);
  } catch (error) {
    reportError(error, {
      scope: 'resolve-citations'
    });
    return [];
  }
}

// Extracts BreezeEngineCitations from SourcesListResponseSection display blocks.
// Using this as the primary citation source is more reliable than parsing the
// text-block JSON because display blocks are never substituted by large-output
// file handling (only the TextChirpContentBlock is replaced with a file-path
// message when a tool result exceeds the 15k-token threshold).
export function extractCitationsFromSourcesList(messages) {
  return messages.flatMap(msg => msg.blocks).filter(block => block.type === 'DISPLAY_ONLY').flatMap(getSourcesSectionsFromBlock).flatMap(section => asArray(section === null || section === void 0 ? void 0 : section.sources)).flatMap(item => {
    var _source$data;
    const source = item;
    if (typeof source.id !== 'string' || !source.id.startsWith('cite-')) return [];
    const data = source.data;
    const fileUrl = data === null || data === void 0 ? void 0 : data.fileUrl;
    if ((data === null || data === void 0 ? void 0 : data.source) === 'FILE_UPLOAD' && typeof fileUrl === 'string' && fileUrl) {
      return [{
        id: source.id,
        fileUrl,
        title: typeof source.primaryText === 'string' ? source.primaryText : source.id,
        citationType: 'FileUploadCitation'
      }];
    }
    const url = (_source$data = source.data) === null || _source$data === void 0 ? void 0 : _source$data.url;
    if (typeof url !== 'string' || !url) return [];
    return [{
      id: source.id,
      url,
      title: typeof source.primaryText === 'string' ? source.primaryText : url,
      citationType: 'WebPageCitation'
    }];
  });
}
function resolveInlineEntries(text, entryMap) {
  const entries = [];
  const seen = new Map();
  let nextIndex = 1;
  const resolved = text.replace(createCiteMarkerRegex(), match => {
    const id = extractCiteId(match);
    if (seen.has(id)) return `[RESPONSE_SECTION_${seen.get(id)}]`;
    const entry = entryMap.get(id);
    if (!entry) return match;
    const index = nextIndex++;
    seen.set(id, index);
    entries.push(entry);
    return `[RESPONSE_SECTION_${index}]`;
  });
  return {
    text: resolved,
    entries
  };
}
function collectComposableCardChipCitations(inlineEntries, allCitations) {
  if (allCitations.length === 0) return [];
  const result = [];
  const seenIds = new Set();
  const citeMarkerRegex = createCiteMarkerRegex();
  for (const entry of inlineEntries) {
    if (entry.__typename !== ComposableCardResponseSectionTypename) continue;
    if (!isRecord(entry.value)) continue;
    const {
      bodyBlocks,
      headerBlocks,
      title
    } = entry.value;
    const textsToScan = [];
    if (typeof title === 'string') textsToScan.push(title);
    for (const block of [...asArray(bodyBlocks), ...asArray(headerBlocks)]) {
      if (!isRecord(block)) continue;
      if (block.__typename === TextFieldBlockTypename && typeof block.value === 'string') {
        textsToScan.push(block.value);
      } else if (block.__typename === TimelineBlockTypename && Array.isArray(block.activities)) {
        for (const activity of block.activities) {
          if (isRecord(activity) && typeof activity.description === 'string') {
            textsToScan.push(activity.description);
          }
        }
      }
    }
    for (const text of textsToScan) {
      for (const match of text.matchAll(citeMarkerRegex)) {
        const citeId = extractCiteId(match[0]);
        if (seenIds.has(citeId)) continue;
        const citation = allCitations.find(c => c.id === citeId);
        if ((citation === null || citation === void 0 ? void 0 : citation.displayType) === 'RECORD_CHIP') {
          seenIds.add(citeId);
          result.push(citation);
        }
      }
    }
  }
  return result;
}
export function resolveCitations(messages, preExtractedCitations = [], conversationId, isTurnInProgress) {
  // Always prefer citations from SourcesListResponseSection display blocks:
  // they survive large-output file substitution, unlike text-block JSON.
  // Last writer wins: later SourcesListResponseSection entries (e.g. a second
  // search call that returns updated results) overwrite earlier ones for the
  // same cite-ID. Fill any remaining IDs from preExtractedCitations (text-block
  // JSON path) so tools that don't emit a SourcesListResponseSection still work.
  const entryMap = buildCitationIdToEntryMap(messages);
  const sourcesListById = new Map(extractCitationsFromSourcesList(messages).map(c => [c.id, c]));

  // Per-citation display blocks store the original Citation object (e.g.
  // WebPageCitation) whose title is always the document title. Prefer that
  // title over SourceItem.primaryText, which may be set to the app name
  // (e.g. "Google Drive") after a future BE field-assignment change.
  entryMap.forEach((entry, id) => {
    var _entry$value2;
    const citation = sourcesListById.get(id);
    const blockTitle = (_entry$value2 = entry.value) === null || _entry$value2 === void 0 ? void 0 : _entry$value2.title;
    if (citation && typeof blockTitle === 'string' && blockTitle) {
      sourcesListById.set(id, Object.assign({}, citation, {
        title: blockTitle
      }));
    }
  });
  const allCitations = [...sourcesListById.values(), ...preExtractedCitations.filter(c => !sourcesListById.has(c.id))];

  // Fold in per-citation DISPLAY_ONLY entries (WebPageCitation, CrmObjectCitation,
  // RichContentCitation, AppCitation) that aren't already covered by a
  // SourcesListResponseSection or text-block JSON. Without this, their [cite-xxx]
  // markers escape convertToSearchResultMarkers and land in dynamicSectionContainers,
  // which has no renderer for citation typenames and drops them silently.
  const knownCitationIds = new Set(allCitations.map(citation => citation.id));
  entryMap.forEach((entry, id) => {
    if (knownCitationIds.has(id)) return;
    const citation = convertDisplayOnlyEntryToCitation(entry);
    if (citation) {
      allCitations.push(citation);
      knownCitationIds.add(id);
    }
  });
  if (allCitations.length === 0 && entryMap.size === 0) {
    // Fast path: skip processing entirely unless stray [cite-xxx] markers are
    // present in ASSISTANT text (those need to be suppressed even with no
    // citations registered).
    const hasStrayMarkers = messages.some(msg => msg.role === 'ASSISTANT' && msg.blocks.some(block => block.type === 'TEXT' && createCiteMarkerRegex().test(block.text)));
    if (!hasStrayMarkers) return messages;
  }

  // Chip citations must reach the section formatter so chips render with
  // popover data when the LLM embeds [CRM_RECORD:...] markers in composable
  // card body values without referencing [cite-xxx] in prose. To prevent
  // cross-turn bleed, each chip citation is attached to at most one ASSISTANT
  // text block — the first one that references or needs it.
  const chipCitations = preExtractedCitations.filter(citation => citation.displayType === 'RECORD_CHIP');
  const consumedChipIds = new Set();
  const consumedCitationIds = new Set();
  if (entryMap.size > 0) {
    for (const msg of messages) {
      if (msg.role !== 'ASSISTANT') continue;
      for (const block of msg.blocks) {
        var _block$text$match;
        if (block.type !== 'TEXT') continue;
        const markers = (_block$text$match = block.text.match(createCiteMarkerRegex())) !== null && _block$text$match !== void 0 ? _block$text$match : [];
        for (const marker of markers) {
          const citationId = extractCiteId(marker);
          if (entryMap.has(citationId)) {
            consumedCitationIds.add(citationId);
          }
        }
      }
    }
  }
  let anyChanged = false;
  const result = messages.map(msg => {
    if (msg.role !== 'ASSISTANT') return msg;
    let changed = false;
    const newBlocks = [];
    for (const block of msg.blocks) {
      if (block.type === 'TEXT') {
        const hasCiteMarkers = createCiteMarkerRegex().test(block.text);
        const freshChipCitations = chipCitations.filter(chip => !consumedChipIds.has(chip.id));
        if (!hasCiteMarkers) {
          if (freshChipCitations.length === 0) {
            newBlocks.push(block);
            continue;
          }
          // No inline cite markers, but attach fresh chip citations so
          // crmRecordChips is populated for any [CRM_RECORD:...] markers in
          // associated sections.
          freshChipCitations.forEach(chip => consumedChipIds.add(chip.id));
          changed = true;
          anyChanged = true;
          newBlocks.push(Object.assign({}, block, {
            citations: freshChipCitations
          }));
          continue;
        }
        const {
          text: afterCitations,
          citations: resolvedCitations
        } = convertToSearchResultMarkers(block.text, allCitations);
        const {
          text: afterEntries,
          entries: rawInlineEntries
        } = resolveInlineEntries(afterCitations, entryMap);

        // Resolve [cite-xxx] markers in inline entries' value.text fields.
        // Tools like visualizeNonCrmResults produce LlmResponseSection entries with
        // [cite-xxx] markers in their text that the section renderer cannot display —
        // it only understands [SEARCH_RESULT_N] and [CRM_RECORD:...].
        const inlineEntries = allCitations.length > 0 ? rawInlineEntries.map(entry => {
          const value = entry.value;
          const text = typeof value.text === 'string' ? value.text : null;
          if (!text || !createCiteMarkerRegex().test(text)) return entry;
          const {
            text: resolvedText,
            citations: entryCitations
          } = convertToSearchResultMarkers(text, allCitations);
          if (resolvedText === text) return entry;
          return Object.assign({}, entry, {
            resolvedText,
            resolvedCitations: entryCitations
          });
        }) : rawInlineEntries;

        // Suppress any [cite-xxx] markers that neither resolution pass could
        // match. They would otherwise render as literal text in the UI.
        let suppressedCount = 0;
        const finalText = afterEntries.replace(createCiteMarkerRegex(), match => {
          suppressedCount++;
          if (!isTurnInProgress && !reportedMarkers.has(match)) {
            reportedMarkers.add(match);
            reportError(new Error('Unresolved cite marker'), {
              scope: 'resolve-citations',
              fingerprint: ['ERROR_NO_MATCHING_CITATION_ID'],
              extra: {
                citeMarker: match,
                conversationId,
                portalId: PortalIdParser.get()
              }
            });
          }
          return '';
        });

        // Append fresh chip citations not already produced by convertToSearchResultMarkers
        // (chips whose [cite-xxx] appeared in prose are already in resolvedCitations).
        const extraChipCitations = freshChipCitations.filter(chip => !resolvedCitations.some(rc => rc.id === chip.id));
        const blockCitations = [...resolvedCitations, ...extraChipCitations, ...collectComposableCardChipCitations(rawInlineEntries, allCitations)];
        blockCitations.forEach(c => {
          if (c.displayType === 'RECORD_CHIP') consumedChipIds.add(c.id);
        });
        if (blockCitations.length === 0 && inlineEntries.length === 0 && suppressedCount === 0) {
          newBlocks.push(block);
          continue;
        }
        changed = true;
        anyChanged = true;
        newBlocks.push(Object.assign({}, block, {
          text: finalText
        }, blockCitations.length > 0 ? {
          citations: blockCitations
        } : {}, inlineEntries.length > 0 ? {
          entries: inlineEntries
        } : {}));
      } else if (block.type === 'DISPLAY_ONLY' && (entryMap.size > 0 || allCitations.length > 0)) {
        var _block$entries3;
        const blockEntries = (_block$entries3 = block.entries) !== null && _block$entries3 !== void 0 ? _block$entries3 : [];

        // Drop companion citation entries: they are already registered in
        // allCitations above, and they have no standalone renderer — leaving them
        // on the block makes the consuming surface render an error card per
        // citation whenever the model never cited them in prose.
        const filteredEntries = entryMap.size > 0 ? blockEntries.filter(entry => {
          if (entry.citationId && consumedCitationIds.has(entry.citationId)) return false;
          if (isCitationCompanionEntry(entry)) return false;
          return true;
        }) : blockEntries;

        // Resolve [cite-xxx] markers in non-citation entry text fields.
        // Tools like visualizeNonCrmResults emit LlmResponseSection entries whose text
        // contains [cite-xxx] markers that the section renderer cannot display —
        // it only understands [SEARCH_RESULT_N] and [CRM_RECORD:...].
        const resolvedEntries = allCitations.length > 0 ? filteredEntries.map(entry => {
          if (entry.citationId) return entry;
          const value = entry.value;
          const text = typeof value.text === 'string' ? value.text : null;
          const hasCiteMarkers = !!text && createCiteMarkerRegex().test(text);
          if (!text || !hasCiteMarkers) return entry;
          const {
            text: resolvedText,
            citations: resolvedCitations
          } = convertToSearchResultMarkers(text, allCitations);
          if (resolvedText === text) return entry;
          return Object.assign({}, entry, {
            resolvedText,
            resolvedCitations
          });
        }) : filteredEntries;
        if (resolvedEntries.length === blockEntries.length && resolvedEntries.every((entry, i) => entry === blockEntries[i])) {
          newBlocks.push(block);
          continue;
        }
        changed = true;
        anyChanged = true;
        if (resolvedEntries.length > 0 || block.text !== undefined) {
          newBlocks.push(Object.assign({}, block, {
            entries: resolvedEntries
          }));
        }
      } else {
        newBlocks.push(block);
      }
    }
    if (!changed) return msg;
    return Object.assign({}, msg, {
      blocks: newBlocks
    });
  });
  return anyChanged ? result : messages;
}